import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Wallet, Trash2, Copy, Loader2, ExternalLink, Pencil, Star } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const MerchantWallets = () => {
  const [wallets, setWallets] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [form, setForm] = useState({ address: '', label: '', network_id: '', enabled_tokens: [] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, networksRes, tokensRes] = await Promise.all([
        axios.get(`${API}/wallets`),
        axios.get(`${API}/networks`),
        axios.get(`${API}/networks/all-tokens`)
      ]);
      setWallets(walletsRes.data);
      const activeNetworks = networksRes.data.filter(n => n.is_active !== false);
      setNetworks(activeNetworks);
      const activeTokens = tokensRes.data.filter(t => t.is_active !== false);
      setTokens(activeTokens);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getTokensForNetwork = (networkId) => {
    return tokens.filter(t => t.network_id === networkId && t.is_active !== false);
  };

  const openAddDialog = () => {
    setEditingWallet(null);
    const defaultNetwork = networks[0]?.id || '';
    const defaultTokens = getTokensForNetwork(defaultNetwork).map(t => t.id);
    setForm({ address: '', label: '', network_id: defaultNetwork, enabled_tokens: defaultTokens });
    setDialogOpen(true);
  };

  const openEditDialog = (wallet) => {
    setEditingWallet(wallet);
    const networkTokens = getTokensForNetwork(wallet.network_id);
    const enabledTokens = wallet.enabled_tokens || networkTokens.map(t => t.id);
    setForm({ address: wallet.address, label: wallet.label, network_id: wallet.network_id, enabled_tokens: enabledTokens });
    setDialogOpen(true);
  };

  const handleNetworkChange = (networkId) => {
    const networkTokens = getTokensForNetwork(networkId);
    setForm({ ...form, network_id: networkId, enabled_tokens: networkTokens.map(t => t.id) });
  };

  const toggleToken = (tokenId) => {
    setForm(prev => {
      const enabled = prev.enabled_tokens.includes(tokenId)
        ? prev.enabled_tokens.filter(id => id !== tokenId)
        : [...prev.enabled_tokens, tokenId];
      return { ...prev, enabled_tokens: enabled };
    });
  };

  const toggleAllTokens = (networkId) => {
    const networkTokens = getTokensForNetwork(networkId);
    const allSelected = networkTokens.every(t => form.enabled_tokens.includes(t.id));
    setForm(prev => ({
      ...prev,
      enabled_tokens: allSelected ? [] : networkTokens.map(t => t.id)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.network_id) { toast.error('Please select a network'); return; }
    if (form.enabled_tokens.length === 0) { toast.error('Please enable at least one token'); return; }
    setSaving(true);
    try {
      if (editingWallet) {
        await axios.put(`${API}/wallets/${editingWallet.id}`, {
          label: form.label,
          enabled_tokens: form.enabled_tokens
        });
        toast.success('Wallet updated!');
      } else {
        await axios.post(`${API}/wallets`, {
          address: form.address,
          label: form.label,
          network_id: form.network_id,
          enabled_tokens: form.enabled_tokens
        });
        toast.success('Wallet added!');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save wallet');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (walletId) => {
    if (!confirm('Are you sure you want to delete this wallet?')) return;
    try {
      await axios.delete(`${API}/wallets/${walletId}`);
      toast.success('Wallet deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete wallet');
    }
  };

  const setAsDefault = async (walletId) => {
    try {
      await axios.post(`${API}/wallets/${walletId}/set-default`);
      toast.success('Default wallet updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to set default wallet');
    }
  };

  const toggleActive = async (walletId, currentlyActive) => {
    try {
      await axios.patch(`${API}/wallets/${walletId}/toggle-active`);
      toast.success(currentlyActive ? 'Wallet deactivated' : 'Wallet activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update wallet status');
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Address copied!');
  };

  const getNetworkInfo = (networkId) => {
    return networks.find(n => n.id === networkId) || { name: networkId, explorer_url: '' };
  };

  const selectedNetwork = getNetworkInfo(form.network_id);
  const selectedNetworkTokens = getTokensForNetwork(form.network_id);

  return (
    <MerchantLayout title="Wallets">
      <div className="space-y-6" data-testid="wallets-page">
        <div className="flex items-center justify-between pb-6">
          <div>
            <p className="text-muted-foreground">Manage your crypto wallet addresses for receiving payments</p>
          </div>
          <Button className="bg-primary text-white" disabled={networks.length === 0} onClick={openAddDialog} data-testid="add-wallet-dialog-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Wallet
          </Button>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingWallet ? 'Edit Wallet' : 'Add Wallet Address'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="label">Label</Label>
                <Input
                  id="label"
                  placeholder="Main Wallet, Store Wallet..."
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  required
                  data-testid="wallet-label-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="network">Network</Label>
                <Select value={form.network_id} onValueChange={handleNetworkChange} disabled={!!editingWallet}>
                  <SelectTrigger data-testid="wallet-network-select">
                    <SelectValue placeholder="Select network" />
                  </SelectTrigger>
                  <SelectContent>
                    {networks.map((network) => (
                      <SelectItem key={network.id} value={network.id}>
                        {network.name} ({network.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.network_id && selectedNetworkTokens.length > 0 && (
                <div className="space-y-3 p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Supported Tokens on {selectedNetwork.name}:</p>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => toggleAllTokens(form.network_id)}
                      data-testid="toggle-all-tokens"
                    >
                      {selectedNetworkTokens.every(t => form.enabled_tokens.includes(t.id)) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedNetworkTokens.map((token) => (
                      <label
                        key={token.id}
                        className="flex items-center gap-2 p-2 rounded-md bg-background border border-border hover:border-primary/40 cursor-pointer transition-colors"
                        data-testid={`token-checkbox-${token.symbol}`}
                      >
                        <Checkbox
                          checked={form.enabled_tokens.includes(token.id)}
                          onCheckedChange={() => toggleToken(token.id)}
                        />
                        <span className="text-sm font-medium">{token.symbol}</span>
                        <span className="text-xs text-muted-foreground">{token.name}</span>
                      </label>
                    ))}
                  </div>
                  {form.enabled_tokens.length === 0 && (
                    <p className="text-xs text-destructive">Please enable at least one token</p>
                  )}
                </div>
              )}
              {form.network_id && selectedNetworkTokens.length === 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="no-tokens-warning">
                  <p className="text-xs font-medium text-amber-400">No tokens configured for this network</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Ask your admin to add tokens for this network in Admin &gt; Networks &gt; Tokens tab.</p>
                </div>
              )}

              {!editingWallet && (
                <div className="space-y-2">
                  <Label htmlFor="address">Wallet Address</Label>
                  <Input
                    id="address"
                    placeholder={form.network_id === 'tron' ? 'T...' : '0x...'}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    required
                    data-testid="wallet-address-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your wallet address on {selectedNetwork.name || 'selected network'}. This is where you'll receive payments.
                  </p>
                </div>
              )}

              <Button type="submit" className="w-full bg-primary text-white" disabled={saving} data-testid="wallet-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingWallet ? 'Update Wallet' : 'Add Wallet'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {networks.length === 0 && !loading && (
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardContent className="py-4">
              <p className="text-yellow-500 text-sm">
                No networks are configured yet. Please contact the administrator to set up supported networks.
              </p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : wallets.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Wallet className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-base font-semibold mb-2">No Wallets Added</h3>
              <p className="text-muted-foreground mb-4">Add your first wallet address to start accepting payments</p>
              {networks.length > 0 && (
                <Button onClick={openAddDialog} className="bg-primary text-white">
                  <Plus className="h-4 w-4 mr-2" /> Add Your First Wallet
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {wallets.filter(w => w.network_active !== false).map((wallet) => {
              const networkInfo = getNetworkInfo(wallet.network_id);
              const networkTokens = getTokensForNetwork(wallet.network_id);
              const enabledTokens = wallet.enabled_tokens || networkTokens.map(t => t.id);

              return (
                <Card key={wallet.id} className={`border-border bg-card ${wallet.is_default ? 'ring-1 ring-primary/40' : ''}`} data-testid={`wallet-card-${wallet.id}`}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base font-semibold">{wallet.label}</CardTitle>
                      {wallet.is_default && (
                        <span className="text-[10px] px-2 py-0.5 bg-primary/15 text-primary rounded-full font-medium flex items-center gap-1" data-testid={`default-badge-${wallet.id}`}>
                          <Star className="h-3 w-3 fill-current" /> Default
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="flex items-center gap-1.5 mr-2" data-testid={`toggle-active-${wallet.id}`}>
                        <Switch
                          checked={wallet.is_active !== false}
                          onCheckedChange={() => toggleActive(wallet.id, wallet.is_active !== false)}
                        />
                        <span className={`text-xs ${wallet.is_active !== false ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                          {wallet.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {!wallet.is_default && (
                        <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-muted-foreground hover:text-primary" onClick={() => setAsDefault(wallet.id)} title="Set as default wallet" data-testid={`set-default-${wallet.id}`}>
                          <Star className="h-3.5 w-3.5 mr-1" /> Set Default
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => copyAddress(wallet.address)} data-testid={`copy-wallet-${wallet.id}`}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      {networkInfo.explorer_url && (
                        <a href={`${networkInfo.explorer_url}/address/${wallet.address}`} target="_blank" rel="noopener noreferrer">
                          <Button size="icon" variant="ghost"><ExternalLink className="h-4 w-4" /></Button>
                        </a>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => openEditDialog(wallet)} data-testid={`edit-wallet-${wallet.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDelete(wallet.id)} data-testid={`delete-wallet-${wallet.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <code className="text-sm font-mono text-muted-foreground break-all block mb-3">
                      {wallet.address}
                    </code>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                        {networkInfo.name}
                      </span>
                    </div>
                    {networkTokens.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-2">Enabled Tokens:</p>
                        <div className="flex flex-wrap gap-1">
                          {networkTokens.map((token) => {
                            const isEnabled = enabledTokens.includes(token.id);
                            return (
                              <span key={token.id} className={`text-xs px-2 py-0.5 rounded ${isEnabled ? 'bg-primary/10 text-primary font-medium' : 'bg-muted text-muted-foreground line-through opacity-50'}`}>
                                {token.symbol}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {wallets.some(w => w.network_active === false) && (
            <p className="text-xs text-muted-foreground mt-4">
              {wallets.filter(w => w.network_active === false).length} wallet(s) hidden — their network has been deactivated by the admin.
            </p>
          )}
          </>
        )}
      </div>
    </MerchantLayout>
  );
};

export default MerchantWallets;
